import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from './theme';

// Animations
export const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

export const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

export const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.9; }
`;

export const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.3); }
  50% { box-shadow: 0 0 30px rgba(255, 215, 0, 0.5); }
`;

// Page Wrapper
export const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
`;

// Hero Section
export const HeroSection = styled.section`
  background: linear-gradient(135deg, #1a2332 0%, #2c3e50 50%, #1a2332 100%);
  padding: 120px 20px 80px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(16, 163, 127, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 200%;
    height: 100%;
    top: 0;
    left: -50%;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(255, 215, 0, 0.03) 45%, 
      rgba(255, 215, 0, 0.06) 50%, 
      rgba(255, 215, 0, 0.03) 55%, 
      transparent 100%
    );
    animation: ${shimmer} 15s infinite linear;
    pointer-events: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 100px 15px 60px;
  }
`;

// Container
export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

// Section Title
export const PageTitle = styled.h1`
  font-size: clamp(42px, 6vw, 72px);
  font-weight: 900;
  text-align: center;
  margin-bottom: 30px;
  letter-spacing: -2px;
  line-height: 1.1;
  
  .gold {
    color: #ffd700;
    text-shadow: 0 0 30px rgba(255, 215, 0, 0.5);
    animation: ${pulse} 3s ease-in-out infinite;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(32px, 8vw, 48px);
    letter-spacing: -1px;
  }
`;

export const PageSubtitle = styled.p`
  font-size: clamp(20px, 3vw, 28px);
  text-align: center;
  margin-bottom: 60px;
  line-height: 1.5;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  font-weight: 400;
  opacity: 0.95;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(18px, 4vw, 22px);
    margin-bottom: 40px;
  }
`;

// Cards
export const PremiumCard = styled.div<{ $variant?: 'primary' | 'secondary' | 'accent' }>`
  background: ${props => {
    switch(props.$variant) {
      case 'primary': return 'linear-gradient(135deg, rgba(255, 215, 0, 0.08) 0%, rgba(255, 215, 0, 0.04) 100%)';
      case 'secondary': return 'linear-gradient(135deg, rgba(16, 163, 127, 0.08) 0%, rgba(16, 163, 127, 0.04) 100%)';
      case 'accent': return 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)';
      default: return 'white';
    }
  }};
  border: 3px solid ${props => {
    switch(props.$variant) {
      case 'primary': return 'rgba(255, 215, 0, 0.3)';
      case 'secondary': return 'rgba(16, 163, 127, 0.3)';
      default: return 'rgba(255, 255, 255, 0.2)';
    }
  }};
  border-radius: 30px;
  padding: 50px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 35px 80px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 40px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 30px 20px;
    border-radius: 20px;
  }
`;

// Buttons
export const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #1a1a1a;
  padding: 22px 50px;
  border: none;
  border-radius: 50px;
  font-size: clamp(18px, 2.5vw, 22px);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
  position: relative;
  overflow: hidden;
  display: inline-block;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    transform: translate(-50%, -50%);
    transition: width 0.5s, height 0.5s;
  }
  
  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 20px 50px rgba(255, 215, 0, 0.5);
    
    &::before {
      width: 400px;
      height: 400px;
    }
  }
  
  &:active {
    transform: translateY(-1px) scale(1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 18px 40px;
    font-size: clamp(16px, 3vw, 18px);
    letter-spacing: 1px;
  }
`;

export const SecondaryButton = styled(PrimaryButton)`
  background: transparent;
  border: 3px solid #10a37f;
  color: #10a37f;
  box-shadow: 0 10px 30px rgba(16, 163, 127, 0.2);
  
  &:hover {
    background: rgba(16, 163, 127, 0.1);
    box-shadow: 0 15px 40px rgba(16, 163, 127, 0.3);
  }
`;

// Form Elements
export const FormGroup = styled.div`
  margin-bottom: 30px;
`;

export const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  span.required {
    color: #ff4444;
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 18px 24px;
  font-size: 16px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 15px;
  background: white;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
    box-shadow: 0 0 0 4px rgba(255, 215, 0, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

export const Textarea = styled.textarea`
  width: 100%;
  padding: 18px 24px;
  font-size: 16px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 15px;
  background: white;
  transition: all 0.3s;
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
    box-shadow: 0 0 0 4px rgba(255, 215, 0, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 18px 24px;
  font-size: 16px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 15px;
  background: white;
  transition: all 0.3s;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%23666' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 20px center;
  padding-right: 50px;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
    box-shadow: 0 0 0 4px rgba(255, 215, 0, 0.1);
  }
`;

// Grid Layouts
export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

export const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

// Section
export const Section = styled.section<{ $background?: string; $dark?: boolean }>`
  padding: 100px 20px;
  background: ${props => props.$background || 'white'};
  color: ${props => props.$dark ? 'white' : '#333'};
  position: relative;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 15px;
  }
`;

// Alert/Info Boxes
export const InfoBox = styled.div<{ $variant?: 'info' | 'success' | 'warning' | 'error' }>`
  padding: 20px 25px;
  border-radius: 15px;
  margin: 20px 0;
  border-left: 5px solid ${props => {
    switch(props.$variant) {
      case 'success': return '#10a37f';
      case 'warning': return '#ffd700';
      case 'error': return '#ff4444';
      default: return '#2196f3';
    }
  }};
  background: ${props => {
    switch(props.$variant) {
      case 'success': return 'rgba(16, 163, 127, 0.1)';
      case 'warning': return 'rgba(255, 215, 0, 0.1)';
      case 'error': return 'rgba(255, 68, 68, 0.1)';
      default: return 'rgba(33, 150, 243, 0.1)';
    }
  }};
  
  h4 {
    margin-bottom: 10px;
    font-size: 18px;
    font-weight: 700;
  }
  
  p {
    line-height: 1.6;
  }
`;